import {alpha2ToAlpha3LanguageCode} from '@/common/constants/language';
import {LocationWithWeather, WeatherForecastDay, WeatherForecastLocation} from '@/app/interfaces/weather';
import {LocationPoint} from '@/common/interfaces/location';
import {getTimezoneToUTCOffset} from '@/common/helpers/date/getTimezoneToUTCOffset';
import {timeStringToMinutes} from '@/common/helpers/date/timeStringToMinutes';
import {generateLocationAttributeMask, LocationField} from '@/common/helpers/location/generateLocationAttributeMask';
import {AbstractFetcher} from '@/common/services/fetcher/AbstractFetcher';
import {parseLocationPoint} from '../helpers/locationHelpers';

export const getForecast =
	(fetcher: AbstractFetcher) =>
	async (
		lang: string,
		locationId: string,
		daysCount: number,
		date?: string,

		/**
		 * When server detects that the supplied date is in the future, relative to
		 * the current date in location's Timezone, it falls back to its current date.
		 * This day may not correspond to an actual first day, when data is not available
		 */
		nonFutureDate?: string,
	) => {
		/* eslint-disable @typescript-eslint/naming-convention */
		const postData = {
			lang: alpha2ToAlpha3LanguageCode[lang],
			location_id: locationId,
			forecast_days: daysCount,
			date,
			non_future_date: nonFutureDate,
		};
		/* eslint-enable @typescript-eslint/naming-convention */

		const response = await fetcher.fetch('/api/weather/location/forecast/by_id', postData);

		const forecast: WeatherForecastDay[] = Object.entries(response.forecast).map(
			([dayDate, day]: [string, any]) => ({
				date: dayDate,
				isHoliday: day.is_holiday,
				condition: day.condition,
				temperature: {
					min: day.temp.min,
					max: day.temp.max,
				},
				now: day.now
					? {
							temperature: day.now.temp,
							temperatureFeel: day.now.temp_feels,
							waterTemperature: day.now.temp_water,
							condition: day.now.condition,
						}
					: undefined,
				hours: day.hours.map((item: any) => ({
					hour: item.hour,
					condition: item.condition,
					temperature: item.temp,
					temperatureFeel: item.temp_feels,
					pressure: item.pressure,
					humidity: item.humidity,
					wind: {
						direction: item.wind?.dir,
						speed: item.wind?.speed || 0,
					},
					precipitation: item.precip || 0,
					alerts: item.alerts || [],
				})),
				verbal: {
					generic: day.verbal?.gen,
					folk: day.verbal?.folk,
				},
				sunTime: {
					sunrise: day.sun.rises_at ? timeStringToMinutes(day.sun.rises_at) : undefined,
					sunset: day.sun.sets_at ? timeStringToMinutes(day.sun.sets_at) : undefined,
					isPolarDay: !!day.sun.is_polar_day,
					isPolarNight: !!day.sun.is_polar_night,
				},
				historic: day.temp.historic
					? {
							min: {
								temperature: day.temp.historic.min.t,
								year: day.temp.historic.min.years.join(', '),
							},
							max: {
								temperature: day.temp.historic.max.t,
								year: day.temp.historic.max.years.join(', '),
							},
						}
					: undefined,
			}),
		);

		// Calculate timezone offset by the zone name or use the defined offset from the server as a fallback
		let timezoneOffset;
		const calculatedTimezoneOffset = getTimezoneToUTCOffset(response.location.tz_name);
		if (calculatedTimezoneOffset !== null) {
			timezoneOffset = calculatedTimezoneOffset;
		} else {
			timezoneOffset = Object.values<any>(response.forecast).find(day => day.now)?.now.current_utc_tz_offset || 0;
			timezoneOffset *= 1000;
		}

		const location: WeatherForecastLocation = {
			...parseLocationPoint(response.location),
			description: response.location.description,
			timezoneOffset,
			timezoneName: response.location.tz_name,
			coordinates: response.location.coordinates,
			superior: {
				continent: response.superior?.continent ? parseLocationPoint(response.superior?.continent) : undefined,
				country: response.superior?.country
					? {...parseLocationPoint(response.superior?.country), alpha2Code: response.superior.country.code_a2}
					: undefined,
				region: response.superior?.region ? parseLocationPoint(response.superior?.region) : undefined,
				district: response.superior?.district ? parseLocationPoint(response.superior?.district) : undefined,
				subDistrict: response.superior?.sub_district
					? parseLocationPoint(response.superior?.sub_district)
					: undefined,
			},
		};

		return {forecast, location};
	};

export const getWeatherByLocationsIds =
	(fetcher: AbstractFetcher) =>
	async (
		locations: string[],
		lang: string,
		locationFields: LocationField[] = [],
		getTemperatureRange?: boolean, // TODO improve logic, maybe use bitmask
	) => {
		let attributes = generateLocationAttributeMask(locationFields);
		if (getTemperatureRange) {
			attributes |= parseInt('1000000', 2);
		}

		const response = await fetcher.fetch<{locations: any[]}>('/api/weather/location/list/by_id', {
			locations,
			lang: alpha2ToAlpha3LanguageCode[lang],
			attributes,
		});

		return response.locations.map(item => ({
			location: parseLocationPoint(item),
			weather: {
				condition: item.weather.condition as number,
				temperature: item.weather.temp as number,
				temperatureMin: item.weather.temp_min as number | undefined,
				temperatureMax: item.weather.temp_max as number | undefined,
				isNight: item.sun === 'night',
			},
		}));
	};

export const getWeatherForLocations =
	(fetcher: AbstractFetcher) => async (locations: LocationPoint[], lang: string, getTemperatureRange?: boolean) => {
		if (!locations.length) {
			return [];
		}

		const ids = locations.map(item => item.id);
		const weatherLocations = await getWeatherByLocationsIds(fetcher)(ids, lang, [], getTemperatureRange);

		const weatherMap = weatherLocations.reduce<Record<string, LocationWithWeather['weather']>>(
			(map, {location, weather}) => ({
				...map,
				[location.id]: weather,
			}),
			{},
		);

		return locations.map(location => ({
			location,
			weather: weatherMap[location.id],
		}));
	};
